import React, { useState } from 'react';
import { Fetcher } from '@this/src/util';
import { Input } from '@this/shared/ui/inputs/input';
import { Button } from '@this/shared/ui/inputs/button';
import { Link } from '@this/shared/ui/navigations/link';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import { Text } from '@this/shared/ui/data_displays/typography';
import type { OneTimePasswordRestartReason } from './one_time_password';
import {
  ONE_TIME_PASSWORD_MESSAGES,
  isOneTimePasswordRestartReason,
  responseErrorCode
} from './one_time_password';

interface Props<T extends object> {
  onRestart: (reason: OneTimePasswordRestartReason | null) => void;
  verifyPath?: string;
  resendPath?: string;
  onVerified?: (response: T) => void;
}

const CODE_LENGTH = 6;

const redirectAfterVerified = (response: { redirect?: string }) => {
  window.location.href = response.redirect || '/dashboard';
};

const OneTimePasswordForm = <T extends object = { redirect?: string }>({
  onRestart,
  verifyPath = '/users/auth/one_time_password',
  resendPath = '/users/auth/one_time_password/resend',
  onVerified = redirectAfterVerified as (response: T) => void
}: Props<T>) => {
  const [code, setCode] = useState<string>('');
  const [errorText, setErrorText] = useState<string | null>(null);
  const [infoText, setInfoText] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);

  const handleError = (e: unknown) => {
    const error = responseErrorCode(e);
    if (isOneTimePasswordRestartReason(error)) {
      onRestart(error);
      return;
    }
    setErrorText(
      (error && (ONE_TIME_PASSWORD_MESSAGES[error] || error)) ||
        '通信環境が不安定です。時間をおいてもう一度お試しください。'
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorText(null);
    setInfoText(null);
    let res: T;
    try {
      res = await Fetcher.post<T>(verifyPath, { code });
    } catch (error) {
      setIsLoading(false);
      handleError(error);
      return;
    }
    setIsLoading(false);
    onVerified(res);
  };

  const handleResend = async () => {
    if (isResending) return;

    setIsResending(true);
    setErrorText(null);
    setInfoText(null);
    try {
      await Fetcher.post(resendPath, {});
      setCode('');
      setInfoText('認証コードを再送しました。');
    } catch (error) {
      handleError(error);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
      {errorText && (
        <Alert variant="danger" style={{ width: '100%' }}>
          {errorText}
        </Alert>
      )}
      {infoText && (
        <Alert variant="info" style={{ width: '100%' }}>
          {infoText}
        </Alert>
      )}
      <Text color="description">
        ご登録のメールアドレスに認証コードを送信しました。
        <br />
        メールに記載された6桁の認証コードを入力してください。
      </Text>
      <form
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}
      >
        <Input
          type="text"
          placeholder="認証コード"
          name="code"
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
          inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: CODE_LENGTH }}
          required
          fullWidth
        />
        <Button type="submit" disabled={code.length !== CODE_LENGTH} loading={isLoading} fullWidth>
          ログイン
        </Button>
      </form>
      <Link size="small" onClick={handleResend}>
        認証コードを再送する
      </Link>
      <Link size="small" onClick={() => onRestart(null)}>
        ログイン画面に戻る
      </Link>
    </div>
  );
};

export default OneTimePasswordForm;
